import React from 'react';

import { ACCOUNT_PAGE_TITLES, type AccountPageId } from '@/lib/accountPaths';
import { useAccountRecord } from './AccountRecordContext';
import { isAccountOwner } from './accountModel';
import { AccountNotice } from './AccountNotice';
import { SmallButton } from './primitives';

/**
 * AR-380 — the page shell every Account page renders inside.
 *
 * The sticky bar under the page title is the second half of the one editing
 * model: while any card on the page is open it carries Save all and Discard;
 * on the page that publishes (Brand + Public Presence) with a draft waiting it
 * carries Preview and Publish changes instead.
 *
 * v15 added two things above the body: the action-required notice (which
 * replaced the overview page) and an optional action row in the header, which
 * Brand + Public Presence uses to carry the Showcase's state and doors.
 */

/** The pages whose cards save a draft rather than a live change. */
const PUBLISHING_PAGES: readonly AccountPageId[] = ['public'];

export const AccountSaveBar: React.FC<{ page: AccountPageId; onPreview?: () => void }> = ({
  page,
  onPreview,
}) => {
  const { openCards, record, saveAll, cancelAll, publish } = useAccountRecord();
  const publishes = PUBLISHING_PAGES.includes(page);

  if (openCards.size) {
    const publicDraft =
      publishes && [...openCards].some((k) => k === 'pub' || k === 'spec' || k === 'cta');
    return (
      <div
        data-account-savebar
        className="sticky top-0 z-20 my-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-orange-300 bg-white px-3.5 py-2 text-[14.5px] font-semibold text-gray-900 shadow-md dark:border-orange-700 dark:bg-[#1A2021] dark:text-gray-100"
      >
        <span>
          {publicDraft
            ? 'Unsaved draft changes on this page'
            : 'Unsaved changes on this page'}
        </span>
        <span className="flex items-center gap-2">
          <SmallButton primary onClick={saveAll}>
            Save all
          </SmallButton>
          <SmallButton onClick={cancelAll}>Discard</SmallButton>
        </span>
      </div>
    );
  }

  if (publishes && record.pubmeta.dirty) {
    return (
      <div
        data-account-savebar="draft"
        className="sticky top-0 z-20 my-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-orange-300 bg-white px-3.5 py-2 text-[14.5px] font-semibold text-gray-900 shadow-md dark:border-orange-700 dark:bg-[#1A2021] dark:text-gray-100"
      >
        <span>Unpublished changes</span>
        <span className="flex items-center gap-2">
          <SmallButton onClick={onPreview}>Preview</SmallButton>
          <SmallButton primary onClick={publish}>
            Publish changes
          </SmallButton>
        </span>
      </div>
    );
  }

  return null;
};

/** The note that says the record on screen is sample data, and puts it back. */
/**
 * AR-381 — the line that says what the reader is looking at.
 *
 * It said "sample data" because that is all AR-380 had. Once the migrated
 * model is answering, saying so would be untrue: the values on the page are
 * the member's own, and "reset form" would suggest it can undo them. The
 * footer names the source instead, and offers the reset only where there is
 * sample data to reset.
 */
export const AccountSampleFooter: React.FC = () => {
  const { reset, isLive } = useAccountRecord();
  if (isLive) {
    return (
      <p className="mt-3.5 text-[14px] text-gray-500 dark:text-gray-400">
        These are your own account details. Fields WorldVia does not hold yet
        still show sample values.
      </p>
    );
  }
  return (
    <p className="mt-3.5 text-[14px] text-orange-800 dark:text-orange-400">
      You can edit this sample data.{' '}
      <button
        type="button"
        onClick={reset}
        className="cursor-pointer underline hover:no-underline"
      >
        Click here to reset form.
      </button>
    </p>
  );
};

export const AccountPageShell: React.FC<{
  page: AccountPageId;
  onPreview?: () => void;
  /** Controls in the page header — the Showcase's state and doors. */
  headerActions?: React.ReactNode;
  children: React.ReactNode;
}> = ({ page, onPreview, headerActions, children }) => {
  const { role } = useAccountRecord();
  const [title, standfirst] = ACCOUNT_PAGE_TITLES[page];
  const sub =
    typeof standfirst === 'function' ? standfirst(isAccountOwner(role)) : standfirst;

  return (
    <div className="px-5 py-5 lg:px-8">
      <div className="mb-1 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[26px] font-bold tracking-tight text-gray-900 dark:text-gray-100">
            {title}
          </h1>
          <p className="mt-0.5 text-[15.5px] text-gray-500 dark:text-gray-400">{sub}</p>
        </div>
        {headerActions ? (
          <div className="flex flex-wrap items-center gap-1.5">{headerActions}</div>
        ) : null}
      </div>
      <AccountSaveBar page={page} onPreview={onPreview} />
      <AccountNotice />
      {children}
      <AccountSampleFooter />
    </div>
  );
};
